import React from 'react';
import {Box, Chip, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {useTranslation} from 'react-i18next';
import {GenerationListItem} from '../../models/Generation';
import {formatCreatedAt} from './creationFormat';

interface CreationCardProps {
    item: GenerationListItem;
    onOpen: (item: GenerationListItem) => void;
}

const CardContainer = styled(Box)(({theme}) => ({
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease',
    border: '1px solid rgba(255, 255, 255, 0.44)',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

/**
 * A square well via padding-top, NOT `aspect-ratio`: the phone browsers this
 * app is actually used on render the ratio property inconsistently inside a
 * flex/grid child, and a collapsed cell is a broken-looking gallery.
 */
const ImageWell = styled(Box)({
    position: 'relative',
    width: '100%',
    paddingTop: '100%',
    backgroundColor: '#0F2233',
});

const WellContent = styled(Box)({
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

const Thumbnail = styled('img')({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
});

const TitleBar = styled(Box)(({theme}) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: 'linear-gradient(111.13deg, rgba(5, 22, 36, 0.92) 45.71%, rgba(2, 89, 137, 0.92) 117.8%)',
    padding: theme.spacing(1),
}));

const BadgeRow = styled(Box)(({theme}) => ({
    position: 'absolute',
    top: theme.spacing(0.75),
    insetInlineStart: theme.spacing(0.75),
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
}));

const Ellipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
};

/**
 * One generation in the grid.
 *
 * The thumbnail is permanent even after the master is deleted, so an expired
 * row still shows its picture with a badge — a broken image would read as a
 * bug, when in fact the record is intact and only the download is gone.
 */
const CreationCard: React.FC<CreationCardProps> = ({item, onOpen}) => {
    const {t} = useTranslation();
    const isVideo = item.outputFormat === 'mp4';
    const FormatIcon = isVideo ? VideocamIcon : ImageIcon;

    return (
        <CardContainer onClick={() => onOpen(item)} data-testid={`creation-${item.id}`}>
            <ImageWell>
                {item.thumbnailUrl ? (
                    <WellContent>
                        <Thumbnail src={item.thumbnailUrl} alt={item.templateName ?? ''} loading="lazy"/>
                    </WellContent>
                ) : (
                    <WellContent>
                        {item.status === 'failed'
                            ? <ErrorOutlineIcon sx={{color: 'rgba(255,255,255,0.45)', fontSize: 40}}/>
                            : <HourglassEmptyIcon sx={{color: 'rgba(255,255,255,0.45)', fontSize: 40}}/>}
                    </WellContent>
                )}

                <BadgeRow>
                    <Chip
                        size="small"
                        icon={<FormatIcon sx={{fontSize: 14}}/>}
                        label={t(`main.creations.format.${isVideo ? 'mp4' : 'png'}`)}
                        sx={{
                            height: 22, fontSize: 11, color: 'white',
                            backgroundColor: 'rgba(5, 16, 29, 0.75)',
                            '& .MuiChip-icon': {color: 'white'},
                        }}
                    />
                    {item.status !== 'completed' && (
                        <Chip
                            size="small"
                            color={item.status === 'failed' ? 'error' : 'warning'}
                            label={t(`main.creations.status.${item.status}`)}
                            sx={{height: 22, fontSize: 11}}
                        />
                    )}
                    {item.expired && (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={t('main.creations.expired')}
                            sx={{height: 22, fontSize: 11, color: 'white', borderColor: 'rgba(255,255,255,0.6)'}}
                        />
                    )}
                    {item.actionSource === 'whatsapp' && (
                        <WhatsAppIcon
                            aria-label={t('main.creations.source.whatsapp')}
                            data-testid="creation-source-whatsapp"
                            sx={{fontSize: 16, color: '#25D366'}}
                        />
                    )}
                    {item.reported && (
                        <ReportProblemOutlinedIcon
                            aria-label={t('main.creations.reported')}
                            sx={{fontSize: 16, color: '#FFB74D'}}
                        />
                    )}
                </BadgeRow>

                <TitleBar>
                    <Typography variant="body2" sx={{color: 'white', fontWeight: 500, ...Ellipsis}}>
                        {item.templateName ?? t('main.creations.unknownTemplate')}
                    </Typography>
                    <Typography variant="caption" sx={{color: 'rgba(255,255,255,0.7)', display: 'block', ...Ellipsis}}>
                        {`${item.userName ?? t('main.creations.unknownUser')} · ${formatCreatedAt(item.createdAt)}`}
                    </Typography>
                </TitleBar>
            </ImageWell>
        </CardContainer>
    );
};

export default CreationCard;
